import Anchor from '@jetbrains/ring-ui/components/dropdown/anchor'
import Select from '@jetbrains/ring-ui/components/select/select'
import classNames from 'classnames'
import * as React from 'react'

import type {BranchItem, RingSelectAnchorProps} from '../../../types'
import {notNull} from '../../../utils/guards'

import {Z_INDEX_INCREMENT} from './BranchSelect'
import type {BranchMultipleSelectBaseProps, Props} from './BranchSelect.types'
import {createGroup, createItem} from './BranchSelect.utils'

const POPUP_WIDTH = 320
const POPUP_HEIGHT = 320
type DefaultProps = {
  label: string
}
type InnerProps = Props<BranchMultipleSelectBaseProps> & DefaultProps

class BranchMultipleSelect extends React.PureComponent<InnerProps> {
  static defaultProps: DefaultProps = {
    label: 'Select a branch',
  }

  handleSelect = (selected: BranchItem | null | undefined) => {
    const branch = selected?.branch
    this.props.onSelect(branch?.wildcard === true ? null : branch)
  }

  handleDeselect = (selected: BranchItem | null | undefined) => {
    const branch = selected?.branch
    this.props.onDeselect(branch?.wildcard === true ? null : branch)
  }

  customAnchor: (arg0: RingSelectAnchorProps) => React.ReactNode = ({
    wrapperProps,
    buttonProps,
    popup,
  }: RingSelectAnchorProps) => {
    const {className, label} = this.props
    const classes = classNames(className, wrapperProps.className)
    return (
      <span {...wrapperProps} className={classes}>
        <Anchor {...buttonProps}>{label}</Anchor> {popup}
      </span>
    )
  }

  getBranches(): ReadonlyArray<BranchItem | null | undefined> {
    const {branches: branchesAndGroups} = this.props
    const branches =
      branchesAndGroups != null ? branchesAndGroups.filter(branch => !branch.groupFlag) : []
    const defaultBranch = branches.find(branch => branch.default)
    const nonDefaultBranches = branches.filter(branch => !branch.default)
    const activeBranches = nonDefaultBranches.filter(branch => branch.active)
    const inactiveBranches = nonDefaultBranches.filter(branch => !branch.active)
    return [
      ...(defaultBranch != null ? createGroup('Default', [defaultBranch]) : []),
      ...createGroup('Active', activeBranches),
      ...createGroup('Inactive', inactiveBranches),
    ]
  }

  getSelected(): ReadonlyArray<BranchItem> {
    const selected = this.props.selected ?? []
    return selected.map(branch => createItem(branch)).filter(notNull)
  }

  render(): React.ReactNode {
    const {className, currentZindex, ringPopupTarget, disabled, label, directions} = this.props
    const branches = this.getBranches()
    return (
      <Select
        type={Select.Type.CUSTOM}
        multiple
        customAnchor={this.customAnchor}
        label={label}
        filter={{
          placeholder: 'Filter branches',
        }}
        directions={directions}
        data={branches.filter(notNull)}
        selected={this.getSelected()}
        onSelect={this.handleSelect}
        onDeselect={this.handleDeselect}
        className={className}
        popupStyle={
          currentZindex != null
            ? {
                zIndex: currentZindex + Z_INDEX_INCREMENT,
              }
            : undefined
        }
        disableMoveOverflow
        disableScrollToActive
        minWidth={POPUP_WIDTH}
        maxHeight={POPUP_HEIGHT}
        ringPopupTarget={ringPopupTarget}
        disabled={disabled}
      />
    )
  }
}

export default BranchMultipleSelect
